/** The 400 px line panel (screens §P5): the roll-forward, the quarterly trend and, where a decision family moves the line, its chips. */
import { Driver, type Driver as DriverId, type ProductLine } from "@bsim/domain";
import type { RoundReport } from "@bsim/contracts";
import { formatUsdM } from "../format";
import { useDecisionSheet } from "../decisions/DecisionSheet";
import { Link } from "../shell/history";
import { GxSheet } from "../theme/GxSheet";
import { useSheetMode } from "../theme/Popover";
import { GxSparkline } from "../theme/Sparkline";
import { useElementWidth } from "../theme/useElementWidth";
import { LADDER } from "./books";

type RollForward = RoundReport["attribution"]["rollForwards"][number];

const DRIVER_LABELS: Readonly<Partial<Record<DriverId, string>>> = {
  volume: "Volume", rateIndex: "Rate index", spread: "Spread", betaLag: "Deposit beta lag", mix: "Mix", origination: "New lending", paydown: "Paydowns",
  prepay: "Prepayments", maturity: "Maturities", default: "Defaults", chargeOff: "Charge-offs", recovery: "Recoveries", provision: "Provision", fee: "Fees",
  opex: "Expenses", tax: "Tax", mtm: "Bond marks", realizedGain: "Realized gains", swapCarry: "Swap carry", dividend: "Dividends", buyback: "Buybacks",
  issuance: "Issuance", fundingPlug: "Funding", depositFlow: "Deposit flows", eventOneOff: "Events", reclass: "Reclassification",
};

export interface DecisionLink { readonly id: string; readonly label: string; readonly href: string }

/** The trend line at the width its box has (the card's 352 px, or the sheet's on a phone). */
function TrendSpark({ values }: { readonly values: readonly number[] }) {
  const [ref, width] = useElementWidth<HTMLDivElement>(120);
  return <div ref={ref} style={{ margin: "8px 0" }}><GxSparkline values={values} color="var(--c1)" width={Math.min(352, Math.max(120, width))} height={48} /></div>;
}

export function LinePanel({ title, rollForward, expectsRollForward, trend, trendLabel = "Quarterly trend", ladder, decisions, causes, twinTrend, bare = false, onRetry, onClose }: {
  readonly title: string; readonly rollForward: RollForward | undefined; readonly expectsRollForward: boolean;
  readonly trend: readonly { readonly label: string; readonly value: number; readonly text: string }[];
  readonly trendLabel?: string;
  /** A product's balance by reset bucket, and the decisions that move it (chips that open Decide). */
  readonly ladder?: ProductLine["repricingUsd"]; readonly decisions?: readonly DecisionLink[];
  /** Results' side-by-side: what explains the gap to the twin, the twin's own quarters beside yours, and `bare` for a host (a drawer) that supplies the card and its title. */
  readonly causes?: readonly { readonly label: string; readonly text: string }[];
  readonly twinTrend?: readonly { readonly label: string; readonly value: number; readonly text: string }[];
  readonly bare?: boolean;
  readonly onRetry: () => void; readonly onClose: () => void;
}) {
  const sheet = useDecisionSheet();
  const asSheet = useSheetMode() && !bare;
  const body = (
      <div className={`gx-card-b${bare ? "" : " gx-fin-sheetb"}`}>
        {causes === undefined ? null : (
          <>
            <div className="gx-eyebrow">What explains the gap to your twin</div>
            <div className="gx-num" data-testid="financials.causes">{causes.map((c) => <div key={c.label} className="gx-kv"><span className="gx-muted">{c.label}</span><b>{c.text}</b></div>)}</div>
          </>
        )}
        <div className="gx-eyebrow" style={causes === undefined ? undefined : { marginTop: 16 }}>Roll-forward</div>
        {rollForward !== undefined ? (
          <div className="gx-num" data-testid="financials.rollforward">
            <div className="gx-kv"><span>Start of year</span><b>{formatUsdM(rollForward.beginUsd)}</b></div>
            {Driver.options.filter((d) => rollForward.flowsUsd[d] !== undefined).map((d) => (
              <div key={d} className="gx-kv"><span className="gx-muted">{DRIVER_LABELS[d] ?? d}</span><b>{formatUsdM(rollForward.flowsUsd[d] ?? 0, { signed: true })}</b></div>
            ))}
            <div className="gx-kv"><span>End of year</span><b>{formatUsdM(rollForward.endUsd)}</b></div>
          </div>
        ) : expectsRollForward ? (
          <p className="gx-muted" data-testid="financials.rollforward.missing">The roll-forward isn&apos;t available yet. <button type="button" className="gx-linkrow" onClick={onRetry}>Retry</button></p>
        ) : <p className="gx-muted">This line has no roll-forward.</p>}
        <div className="gx-fin-trend">
          <div className="gx-eyebrow" style={{ marginTop: 16 }}>{trendLabel}</div>
          {trend.length > 1 ? <TrendSpark values={trend.map((t) => t.value)} /> : null}
          <div className="gx-num">{trend.map((t) => <div key={t.label} className="gx-kv"><span className="gx-muted">{t.label}</span><b>{t.text}</b></div>)}</div>
        </div>
        {twinTrend === undefined || twinTrend.length === 0 ? null : (
          <>
            <div className="gx-eyebrow" style={{ marginTop: 16 }}>Your twin, same quarters</div>
            <div className="gx-num" data-testid="financials.twintrend">{twinTrend.map((t) => <div key={t.label} className="gx-kv"><span className="gx-muted">{t.label}</span><b>{t.text}</b></div>)}</div>
          </>
        )}
        {ladder !== undefined ? (
          <>
            <div className="gx-eyebrow" style={{ marginTop: 16 }}>When it reprices</div>
            <div className="gx-num" data-testid="financials.ladder">{LADDER.map(([key, label]) => <div key={key} className="gx-kv"><span className="gx-muted">{label}</span><b>{formatUsdM(ladder[key])}</b></div>)}</div>
          </>
        ) : null}
        {decisions !== undefined && decisions.length > 0 ? (
          <>
            <div className="gx-eyebrow" style={{ marginTop: 16 }}>Decisions that move this</div>
            <div className="gx-fin-chips" data-testid="financials.decisions">{decisions.map((d) => sheet === null
              ? <Link key={d.id} href={d.href} className="gx-chip">{d.label}</Link>
              : <button key={d.id} type="button" className="gx-chip" data-testid={`financials.decision.${d.id}`} onClick={() => { if (asSheet) onClose(); sheet.open(d.id); }}>{d.label}</button>)}</div>
          </>
        ) : null}
      </div>
  );
  if (bare) return <div data-testid="financials.panel">{body}</div>;
  // On a phone the line opens as a full-height sheet, not a card beside the table.
  if (asSheet) return <GxSheet title={title} variant="full" onClose={onClose} testId="financials.panel">{body}</GxSheet>;
  return (
    <aside className="gx-card gx-fin-linepanel" aria-label={`${title} detail`} data-testid="financials.panel">
      <div className="gx-card-h"><h2 className="gx-h2">{title}</h2><button type="button" className="gx-x" aria-label="Close panel" onClick={onClose}>×</button></div>
      {body}
    </aside>
  );
}
